// Module permissions map defining which user roles have access to each module
export const ROLE_MODULE_PERMISSIONS: Record<string, string[]> = {
  // 1. Super Admins - Full Platform Access
  SYSTEM_ADMIN: ['*'],
  ORGANIZATION_ADMIN: [
    'modules', 'dashboard', 'occurrences', 'investigations', 'hazards', 'risks', 'actions', 'performance',
    'audits', 'inspections', 'findings', 'capas', 'regulatory', 'regulations', 'obligations', 'matrix',
    'monitoring', 'ingestion', 'surveys', 'tasks_actions', 'workflows', 'reports', 'analytics', 'analysis', 'assets',
    'training', 'documents', 'meetings', 'moc', 'initiatives', 'roles', 'administration', 'integration_attempts', 'help'
  ],

  // 2. Safety Management Roles
  SAFETY_MANAGER: [
    'modules', 'dashboard', 'occurrences', 'investigations', 'hazards', 'risks', 'actions', 'performance',
    'ingestion', 'surveys', 'tasks_actions', 'workflows', 'reports', 'analytics', 'analysis', 'documents', 'meetings',
    'moc', 'initiatives', 'help'
  ],
  INVESTIGATION_MANAGER: [
    'modules', 'dashboard', 'occurrences', 'investigations', 'hazards', 'actions', 'ingestion',
    'tasks_actions', 'reports', 'analysis', 'documents', 'meetings', 'help'
  ],
  RISK_MANAGER: [
    'modules', 'dashboard', 'hazards', 'risks', 'actions', 'performance', 'reports', 'analytics', 'analysis',
    'moc', 'initiatives', 'tasks_actions', 'ingestion', 'meetings', 'help'
  ],

  // 3. Quality & Audit Roles
  QUALITY_MANAGER: [
    'modules', 'dashboard', 'audits', 'inspections', 'findings', 'capas', 'tasks_actions', 'reports',
    'analytics', 'analysis', 'documents', 'meetings', 'initiatives', 'surveys', 'ingestion', 'help'
  ],
  AUDIT_MANAGER: [
    'modules', 'dashboard', 'audits', 'inspections', 'findings', 'capas', 'tasks_actions', 'reports',
    'analysis', 'documents', 'meetings', 'ingestion', 'help'
  ],
  AUDITOR: [
    'modules', 'dashboard', 'audits', 'inspections', 'findings', 'capas', 'tasks_actions', 'analysis', 'meetings', 'ingestion', 'help'
  ],

  // 4. Compliance & Regulatory Roles
  COMPLIANCE_MANAGER: [
    'modules', 'dashboard', 'regulatory', 'regulations', 'obligations', 'matrix', 'monitoring',
    'audits', 'findings', 'tasks_actions', 'reports', 'analysis', 'documents', 'meetings', 'ingestion', 'help'
  ],
  REGULATORY_REPORTING_OFFICER: [
    'modules', 'dashboard', 'regulatory', 'regulations', 'obligations', 'reports', 'analysis', 'meetings', 'ingestion', 'help'
  ],

  // 5. Operations & Specialized Technical Roles
  MOC_MANAGER: [
    'modules', 'dashboard', 'moc', 'risks', 'actions', 'tasks_actions', 'workflows', 'documents',
    'meetings', 'reports', 'analysis', 'ingestion', 'help'
  ],
  TRAINING_MANAGER: [
    'modules', 'dashboard', 'training', 'tasks_actions', 'reports', 'documents', 'meetings', 'ingestion', 'help'
  ],
  DOCUMENT_CONTROLLER: [
    'modules', 'dashboard', 'documents', 'tasks_actions', 'workflows', 'meetings', 'ingestion', 'help'
  ],
  TASK_MANAGER: [
    'modules', 'dashboard', 'tasks_actions', 'actions', 'workflows', 'surveys', 'meetings', 'ingestion', 'help'
  ],
  REPORT_ADMIN: [
    'modules', 'dashboard', 'reports', 'analytics', 'analysis', 'meetings', 'ingestion', 'help'
  ],

  // 6. Operational Frontline Users
  LINE_PILOT: [
    'modules', 'dashboard', 'ingestion', 'occurrences', 'surveys', 'tasks_actions', 'documents',
    'training', 'analysis', 'meetings', 'help'
  ],
  MAINTENANCE_TECH: [
    'modules', 'dashboard', 'ingestion', 'occurrences', 'assets', 'inspections', 'tasks_actions',
    'documents', 'training', 'analysis', 'meetings', 'help'
  ],

  // 7. Base Read-Only User
  READ_ONLY_USER: [
    'modules', 'dashboard', 'occurrences', 'reports', 'documents', 'analysis', 'meetings', 'ingestion', 'help'
  ],
  USER: [
    'modules', 'dashboard', 'ingestion', 'occurrences', 'surveys', 'tasks_actions', 'documents', 'analysis', 'meetings', 'help'
  ]
};

export function isModuleAllowedForRole(role: string, moduleId: string): boolean {
  if (!role) return false;
  // 'modules' overview and 'user_profile' are accessible by any authenticated user
  if (moduleId === 'modules' || moduleId === 'user_profile' || moduleId === 'profile') return true;

  // Resolve module aliases
  let targetModule = moduleId;
  if (moduleId === 'bowtie') targetModule = 'risks';
  if (moduleId === 'vendor_audits') targetModule = 'audits';
  if (moduleId === 'admin') targetModule = 'modules';

  // Safety & Non-safety reporting (ingestion) is the statutory obligation and responsibility
  // of EVERY personnel regardless of role or rank in aviation SMS & QMS.
  if (targetModule === 'ingestion' || targetModule === 'assess_report') return true;

  // Meetings & Committees: accessible to ALL users across the organization, everyone can view and create meetings.
  if (targetModule === 'meetings') return true;

  // Performance (SPI/SPT): accessible to authenticated users; indicator permissions,
  // ownership, and stakeholder visibility are strictly enforced by backend & performance service.
  if (targetModule === 'performance') return true;

  // Resilient role normalization (e.g. "Safety Manager" or "safety-manager" -> "SAFETY_MANAGER")
  const normalizedRole = role.toUpperCase().trim().replace(/[-\s]+/g, '_');
  const allowedModules = ROLE_MODULE_PERMISSIONS[normalizedRole];

  if (!allowedModules) {
    // If unknown role, default fallback allow essential reporting, meetings, and help
    return ['modules', 'dashboard', 'ingestion', 'meetings', 'help'].includes(targetModule);
  }

  if (allowedModules.includes('*')) {
    return true;
  }

  return allowedModules.includes(targetModule);
}

// ============================================================================
// Record-Level / Content-Level Authorization (Content Scope)
// Roles define BOTH module restrictions and content/record visibility scopes
// ============================================================================

export type RoleContentScope = 'ORGANIZATION' | 'OWN_OR_ASSIGNED';

export const ROLE_CONTENT_SCOPES: Record<string, RoleContentScope> = {
  // 1. Super Admins & Organization Administrators - Organization-wide scope
  SYSTEM_ADMIN: 'ORGANIZATION',
  ORGANIZATION_ADMIN: 'ORGANIZATION',

  // 2. Safety & Risk Management - Organization-wide scope for safety oversight
  SAFETY_MANAGER: 'ORGANIZATION',
  INVESTIGATION_MANAGER: 'ORGANIZATION',
  RISK_MANAGER: 'ORGANIZATION',

  // 3. Quality & Compliance - Organization-wide scope for audit & regulatory
  QUALITY_MANAGER: 'ORGANIZATION',
  AUDIT_MANAGER: 'ORGANIZATION',
  AUDITOR: 'ORGANIZATION',
  COMPLIANCE_MANAGER: 'ORGANIZATION',
  REGULATORY_REPORTING_OFFICER: 'ORGANIZATION',

  // 4. Specialized Technical Operations - Organization-wide scope
  MOC_MANAGER: 'ORGANIZATION',
  TRAINING_MANAGER: 'ORGANIZATION',
  DOCUMENT_CONTROLLER: 'ORGANIZATION',
  TASK_MANAGER: 'ORGANIZATION',
  REPORT_ADMIN: 'ORGANIZATION',

  // 5. Frontline & Operational Crew - Content-restricted to own or assigned records
  LINE_PILOT: 'OWN_OR_ASSIGNED',
  MAINTENANCE_TECH: 'OWN_OR_ASSIGNED',
  READ_ONLY_USER: 'OWN_OR_ASSIGNED',
  USER: 'OWN_OR_ASSIGNED',
};

export function normalizeRoleCode(role?: string): string {
  if (!role) return 'USER';
  return role.toUpperCase().trim().replace(/[-\s]+/g, '_');
}

export function getRoleContentScope(role?: string): RoleContentScope {
  if (!role) return 'ORGANIZATION';
  const normalized = normalizeRoleCode(role);
  return ROLE_CONTENT_SCOPES[normalized] || 'OWN_OR_ASSIGNED';
}

export function isRoleContentRestricted(role?: string): boolean {
  if (!role) return false;
  return getRoleContentScope(role) === 'OWN_OR_ASSIGNED';
}


export function isSmsPersonnel(role?: string, department?: string): boolean {
  if (!role && !department) return false;
  const normalizedRole = normalizeRoleCode(role);
  const smsRoles = [
    'SYSTEM_ADMIN',
    'ORGANIZATION_ADMIN',
    'SAFETY_MANAGER',
    'INVESTIGATION_MANAGER',
    'RISK_MANAGER',
  ];
  if (smsRoles.includes(normalizedRole)) return true;
  if (normalizedRole.includes('SAFETY') || normalizedRole.includes('SMS')) return true;

  if (department) {
    const deptUpper = department.toUpperCase().trim();
    if (
      deptUpper === 'SAFETY' ||
      deptUpper === 'SMS' ||
      deptUpper.includes('SAFETY') ||
      deptUpper.includes('SMS')
    ) {
      return true;
    }
  }

  return false;
}
